import type { ReactNode } from "react"

import { Link, Navigate, Outlet, useLocation, useParams } from "react-router-dom"

import useSolarProposal, {
  getSolarProposalErrorStatus,
} from "~/api/hooks/useSolarProposal"
import useSolarProposalCompute from "~/api/hooks/useSolarProposalCompute"
import useSolarProposalDesign from "~/api/hooks/useSolarProposalDesign"
import useSolarProposalEquipment from "~/api/hooks/useSolarProposalEquipment"
import useSolarProposalImpact from "~/api/hooks/useSolarProposalImpact"
import useSolarProposalPricing from "~/api/hooks/useSolarProposalPricing"
import useSolarProposalProductOffering from "~/api/hooks/useSolarProposalProductOffering"
import useSolarProposalSavings from "~/api/hooks/useSolarProposalSavings"

import { SOLAR_LIGHTREACH_FINANCE_TYPE } from "./solar-finance-partner"
import { GET_LIGHTREACH_PRICING_LABEL } from "./solar-lightreach-copy"
import {
  type SolarPresentationData,
  toPresentationHeader,
  toSectionState,
} from "./solar-presentation-data"
import { SOLAR_PROPOSAL_FIXTURE } from "./solar-proposal-fixture"
import { solarHasContract } from "./solar-proposal-headline"
import { SolarProposalLoadState } from "./solar-proposal-load-state"
import {
  SOLAR_SETUP_STEP_LABELS,
  solarContractPath,
  solarProposalPath,
  solarSetupMenuPath,
  solarSetupStepFromPath,
  solarSetupStepPath,
} from "./solar-setup-paths"
import { SolarPresentation } from "./SolarPresentation"
import { SolarProposalPresentation } from "./SolarProposalPresentation"
import { SolarEditDesignAction, SolarReserveNowLink } from "./SolarSellerActions"
import { SolarSetupDrawer } from "./SolarSetupDrawer"
import { SolarSetupMenu } from "./SolarSetupMenu"
import {
  isSolarSummaryPath,
  isSolarSummaryRequired,
  readSummaryReturnTo,
} from "./summary/solar-summary-steps"
import { SolarSummary } from "./summary/SolarSummary"
import { SolarSummaryRedirect } from "./summary/SolarSummaryRedirect"
import { useSolarUpdates } from "./use-solar-updates"
import { PINNED_BATTERY_PROPOSAL_THEME } from "../BatteryOnlyProposal/theme/battery-proposal-theme"
import { ProposalSetupFab } from "../ProposalSetup/ProposalSetupFab"

/** The design reference: every section, rendered from the fixture. */
const SAMPLE_PROPOSAL_ID = `sample`

const ShellFrame = ({ children }: { children: ReactNode }): JSX.Element => (
  <main
    data-testid="solar-proposal-shell"
    data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
    className="bg-bp-page text-bp-text min-h-screen"
  >
    <div className="mx-auto max-w-[var(--max-width-desktop)]">{children}</div>
  </main>
)

/** Keeps the query string and hash, which still mean something on the proposal. */
const BackToProposal = ({ proposalId }: { proposalId: string }): JSX.Element => {
  const { hash, search } = useLocation()
  return (
    <Navigate to={`${solarProposalPath(proposalId)}${search}${hash}`} replace />
  )
}

/** A real Solar Proposal: every section from its own REST read, behind V1's Summary gate. */
const SolarProposalRead = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const { pathname, state: locationState } = useLocation()
  const query = useSolarProposal(proposalId)
  const summaryRequired =
    query.data != null && isSolarSummaryRequired(query.data)
  // Every section's read, handed to the presentation as data: the document
  // itself reads nothing, so a public link can render it from its own read.
  // None is read while the Summary stands in front of the proposal.
  const presentedId = summaryRequired ? undefined : query.data?.id
  const designQuery = useSolarProposalDesign(presentedId)
  const equipmentQuery = useSolarProposalEquipment(presentedId)
  const savingsQuery = useSolarProposalSavings(presentedId)
  const pricingQuery = useSolarProposalPricing(presentedId)
  const impactQuery = useSolarProposalImpact(presentedId)
  // The node's section and variants show only once the Compute read says one is selected.
  const computeQuery = useSolarProposalCompute(presentedId)
  // The plan: a LightReach offer, never Refresh Pricing, sets its price, and
  // without current terms its ledger never shows. A read-only viewer reads
  // no offering; the pricing read's own LightReach terms decide for them.
  const offeringQuery = useSolarProposalProductOffering(
    query.data?.viewerCanUpdate === false ? undefined : presentedId,
  )
  // The pricing read says so for every viewer; the offering covers a gateway
  // that predates the flag.
  const priceSetByOffer =
    pricingQuery.data?.priceSetByOffer === true ||
    // A command may cache a partial answer here (Retry production answers its warnings).
    offeringQuery.data?.partner?.financeType === SOLAR_LIGHTREACH_FINANCE_TYPE
  // Until the plan is known the money waits, unless that read failed or
  // isn't made.
  const planKnown =
    pricingQuery.data?.priceSetByOffer != null ||
    query.data?.viewerCanUpdate === false ||
    offeringQuery.data != null ||
    offeringQuery.isError
  // What's out of date, from the same reads. Only an editor gets the Updates
  // panel, the underline on stale figures and the rail's badges, and only once
  // the proposal itself is shown.
  const updates = useSolarUpdates({
    proposalId,
    reads: {
      design: designQuery.data,
      pricing: pricingQuery.data,
      savings: savingsQuery.data,
      priceSetByOffer,
      // A failed check leaves old data on screen; it no longer says whether
      // it's current.
      unknown: new Set(
        (
          [
            [`production`, designQuery.isError],
            [`pricing`, pricingQuery.isError],
            [`savings`, savingsQuery.isError],
          ] as const
        )
          .filter(([, failed]) => failed)
          .map(([key]) => key),
      ),
    },
    enabled:
      query.data != null &&
      query.data.viewerCanUpdate !== false &&
      !summaryRequired,
  })

  const errorStatus = query.isError
    ? getSolarProposalErrorStatus(query.error)
    : undefined
  // A refetch that loses access must not leave the earlier proposal on screen.
  const accessLost = errorStatus === 403 || errorStatus === 404

  if (!query.data || accessLost) {
    return <SolarProposalLoadState query={query} />
  }

  // V1's two-way Summary rule (VIS-804): an editor of an unconfirmed proposal
  // meets the Summary whichever link they followed, and a confirmed one never
  // does. A deep link's query string and hash survive either way.
  const onSummary = isSolarSummaryPath(pathname, proposalId)
  if (summaryRequired) {
    return onSummary ? (
      <SolarSummary
        proposal={query.data}
        proposalId={proposalId}
        headerFailed={query.isError}
        onRetryHeader={() => void query.refetch()}
      />
    ) : (
      <SolarSummaryRedirect proposalId={proposalId} />
    )
  }
  if (onSummary) {
    // Confirm & open navigates to the same place; whichever lands first, the
    // seller ends up where the link that met the Summary pointed.
    const returnTo = readSummaryReturnTo(locationState, proposalId)
    return returnTo === solarProposalPath(proposalId) ? (
      <BackToProposal proposalId={proposalId} />
    ) : (
      <Navigate to={returnTo} replace />
    )
  }

  // The server's answer, so an actor whose saves would 403 never meets an
  // editing entry point. The rail's route is one of them. Only an explicit
  // denial counts, as on the Battery page. Contract and Financing Status stay
  // open to them, as Battery's do: their own actions are server-gated.
  const readOnly = query.data.viewerCanUpdate === false
  // An Existing Contract can precede the status that names it, so either one
  // swaps Reserve now for Contract status.
  const hasContract =
    query.data.hasExistingContract || solarHasContract(query.data.status)
  const readOnlyStep = solarSetupStepFromPath(pathname, proposalId)
  if (
    readOnly &&
    pathname !== solarProposalPath(proposalId) &&
    readOnlyStep !== `contract` &&
    readOnlyStep !== `financing-status`
  ) {
    return <BackToProposal proposalId={proposalId} />
  }

  const data: SolarPresentationData = {
    header: toPresentationHeader(query.data),
    // The section only: production status reaches the Updates panel instead.
    design: toSectionState({
      data: designQuery.data && { design: designQuery.data.design },
      isError: designQuery.isError,
    }),
    equipment: toSectionState(equipmentQuery),
    savings: toSectionState(savingsQuery),
    // The summary only: the raw stored cost stays with the seller's steps.
    pricingSummary: toSectionState({
      data:
        pricingQuery.data && planKnown
          ? {
              pricingStatus: pricingQuery.data.pricingStatus,
              summary: pricingQuery.data.summary,
              financingSummary: pricingQuery.data.financingSummary ?? null,
              priceSetByOffer,
            }
          : undefined,
      isError: pricingQuery.isError,
    }),
    impact: toSectionState(impactQuery),
    aiCompute: computeQuery.data?.selected
      ? {
          name: computeQuery.data.selected.name,
          computerData: computeQuery.data.selected.computerData,
          income: computeQuery.data.selected.income,
          payout: computeQuery.data.selected.payout ?? null,
        }
      : null,
  }

  return (
    <ShellFrame>
      <SolarProposalPresentation
        data={data}
        heroStatus={
          // Once a contract exists, its status is one click from the headline;
          // read-only viewers may open it too.
          hasContract ? (
            <Link
              to={solarContractPath(proposalId)}
              state={{ returnTo: solarProposalPath(proposalId) }}
              data-testid="solar-contract-status-link"
              className="rounded-full border border-bp-border-strong px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text hover:border-bp-accent hover:text-bp-accent"
            >
              {SOLAR_SETUP_STEP_LABELS.contract}
            </Link>
          ) : null
        }
        sectionActions={
          readOnly
            ? undefined
            : {
                design: (design) => (
                  <SolarEditDesignAction
                    proposalId={proposalId}
                    design={design}
                  />
                ),
                lightReachPricing: (
                  <Link
                    to={solarSetupStepPath(proposalId, `loan-product`)}
                    data-testid="solar-lightreach-pricing-link"
                    className="rounded-full border border-bp-border-strong px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text hover:border-bp-accent hover:text-bp-accent"
                  >
                    {GET_LIGHTREACH_PRICING_LABEL}
                  </Link>
                ),
                compute: (
                  <Link
                    to={solarSetupStepPath(proposalId, `compute`)}
                    data-testid="solar-ai-compute-change"
                    className="rounded-full border border-bp-border-strong px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text hover:border-bp-accent hover:text-bp-accent"
                  >
                    Change in Setup
                  </Link>
                ),
                // Once a contract exists the headline's Contract status takes
                // over, as Battery's card drops Reserve now for its Setup menu.
                reserve: hasContract ? null : (
                  <SolarReserveNowLink proposalId={proposalId} />
                ),
              }
        }
        staleFigures={updates.staleFigures}
        rail={updates.rail}
        chrome={
          <>
            <Outlet />
            {readOnly ? null : (
              <>
                <ProposalSetupFab to={solarSetupMenuPath(proposalId)} />
                {/* Before the rail, as Battery's step drawer outlet is, so the
                    two portals mount in Battery's order. */}
                <SolarSetupDrawer
                  proposal={query.data}
                  proposalId={proposalId}
                />
                <SolarSetupMenu
                  hasPricing={
                    pricingQuery.data != null &&
                    pricingQuery.data.pricingStatus !== `not_calculated`
                  }
                  powurProjectId={query.data.powurProjectId}
                  proposalId={proposalId}
                />
                {updates.panel}
              </>
            )}
          </>
        }
      />
    </ShellFrame>
  )
}

/**
 * Seller page for a Solar Proposal. `sample` keeps the full design fixture as
 * the reference; any other id is a real proposal. Theme tokens are the
 * Battery-Only Proposal's, shared on purpose so both proposal kinds read as one
 * product.
 */
export const SolarProposalShell = (): JSX.Element => {
  const { proposalId = `` } = useParams<{ proposalId: string }>()
  const { pathname } = useLocation()

  if (proposalId === SAMPLE_PROPOSAL_ID) {
    // The design reference has no setup.
    if (pathname !== solarProposalPath(proposalId)) {
      return <BackToProposal proposalId={proposalId} />
    }
    return (
      <ShellFrame>
        <SolarPresentation proposal={SOLAR_PROPOSAL_FIXTURE} />
      </ShellFrame>
    )
  }

  // Keyed so another proposal starts with fresh Updates panel state: the
  // router reuses this element across proposal ids.
  return <SolarProposalRead key={proposalId} proposalId={proposalId} />
}
